import React from 'react';

import { Divider, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { AlineAvatar } from 'src/components';
import type { UsersListRow } from 'src/components/admin/users/UsersList';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getEntityDisplayDetails, getUserName } from 'src/helpers';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  avatar: {
    marginRight: theme.spacing(2),
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
  },
  divider: {
    height: 16,
    margin: theme.spacing(0, 2),
  },
  resendText: {
    ...theme.appTypography.buttonSmall,
    cursor: 'pointer',
  },
  statusContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
  },
}));

export function NameCell({
  row,
  value,
}: GridCellParams<UsersListRow, UsersListRow['name'], string>): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const currentUser = useSelectCurrentUser();
  const [resendOrgInvite] = Apollo.useResendOrgInviteMutation();

  const userName = value ? getUserName(value) : '';
  const { avatarColor } = getEntityDisplayDetails(value);
  const isCurrentUserAdmin = currentUser?.role === Apollo.OrgRole.Admin;

  async function handleResendClick(event: React.MouseEvent): Promise<void> {
    event.stopPropagation();

    if (!value) return;
    const { data } = await resendOrgInvite({ variables: { input: { inviteeId: value.id } } });
    if (!data) return;

    const snackbarKey = uuid4();
    dispatch({
      payload: {
        key: snackbarKey,
        message: 'Invite successfully resent.',
        variant: 'success',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  return (
    <div className={classes.container}>
      <AlineAvatar
        avatarInfo={value}
        className={classes.avatar}
        styles={{ backgroundColor: avatarColor }}
      />
      <div>
        <Typography variant="body1">{userName}</Typography>
        {value?.accountStatus === Apollo.AccountStatus.Pending && !row?.isDeactivated && (
          <div className={classes.statusContainer}>
            <Typography color="textSecondary" variant="caption">
              Invitation pending
            </Typography>
            {isCurrentUserAdmin && (
              <>
                <Divider className={classes.divider} orientation="vertical" />
                <Typography className={classes.resendText} onClickCapture={handleResendClick}>
                  resend
                </Typography>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
